import React from "react";
import { isPlaceholderEmail } from "@atlas/shared";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Filter } from "lucide-react";
import {
  fetchAdminFunnel,
  FUNNEL_STAGES,
  type FunnelCohort,
  type FunnelStage,
  type FunnelStuckUser,
} from "../../api/admin";
import { AtlasPanel } from "../../components/AtlasPanel";
import { StatBlock } from "../../components/StatBlock";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { useAuth } from "../../hooks/useAuth";
import { AdminShell, AccessDenied } from "./AdminTabs";
import { Segmented, formatRelative } from "./usage-ui";

// Admin activation funnel (spec 15): weekly signup cohorts x stages, derived
// on demand from existing tables. Admin accounts excluded, like Usage.

const STAGE_LABEL: Record<FunnelStage, string> = {
  signed_up: "Signed up",
  verified: "Verified",
  source_connected: "Source connected",
  first_activity_processed: "First activity processed",
  first_unlock: "First unlock",
  returned_day7: "Returned day 7",
  pro: "Pro",
};

const STAGE_SHORT: Record<FunnelStage, string> = {
  signed_up: "Signed up",
  verified: "Verified",
  source_connected: "Source",
  first_activity_processed: "Processed",
  first_unlock: "Unlock",
  returned_day7: "Day 7",
  pro: "Pro",
};

type Weeks = "4" | "8" | "12" | "26";

function pct(n: number, d: number): string {
  if (d <= 0) return "0%";
  return `${Math.round((n / d) * 100)}%`;
}

function formatHours(hours: number | null): string {
  if (hours == null) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toLocaleString(undefined, { maximumFractionDigits: 1 })} h`;
  return `${(hours / 24).toLocaleString(undefined, { maximumFractionDigits: 1 })} d`;
}

function weekLabel(week: string): string {
  return new Date(week).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

// ─── Stage bars (aggregate over the window) ───────────────────────────────────

function StageBars({ totals }: { totals: Record<FunnelStage, number> }) {
  const base = Math.max(1, totals.signed_up);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {FUNNEL_STAGES.map((stage, i) => {
        const value = totals[stage];
        const prev = i > 0 ? totals[FUNNEL_STAGES[i - 1]!] : value;
        const width = Math.max(value > 0 ? 2 : 0, (value / base) * 100);
        const fill = stage === "pro" ? "var(--accent)" : stage === "returned_day7" ? "var(--spruce-500)" : "var(--sky-500)";
        return (
          <div key={stage} style={{ display: "grid", gridTemplateColumns: "150px minmax(0, 1fr) 120px", gap: 12, alignItems: "center" }}>
            <span style={{ fontSize: 13, color: "var(--text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {STAGE_LABEL[stage]}
            </span>
            <div style={{ height: 14, borderRadius: "var(--radius-pill)", background: "var(--surface-sunken)", overflow: "hidden" }}>
              <div
                style={{ width: `${width}%`, height: "100%", background: fill, borderRadius: "var(--radius-pill)", transition: "width 0.2s ease" }}
                title={`${STAGE_LABEL[stage]}: ${value.toLocaleString()} (${pct(value, base)} of signups)`}
              />
            </div>
            <span className="tnum" style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "right", fontFamily: "var(--font-mono)" }}>
              {value.toLocaleString()} · {pct(value, base)}
              {i > 0 && prev > 0 && (
                <span style={{ color: "var(--text-faint, var(--text-muted))" }}> ({pct(value, prev)})</span>
              )}
            </span>
          </div>
        );
      })}
      <p style={{ fontSize: 12, color: "var(--text-muted)", margin: "4px 0 0", lineHeight: 1.5 }}>
        Share of signups in the window, then in brackets the share of the previous stage. Stages are not strictly ordered: someone can unlock before verifying.
      </p>
    </div>
  );
}

// ─── Cohort table ─────────────────────────────────────────────────────────────

function CohortTable({ cohorts }: { cohorts: FunnelCohort[] }) {
  const th: React.CSSProperties = { fontSize: 9, textAlign: "right", padding: "0 6px 8px", whiteSpace: "nowrap" };
  const td: React.CSSProperties = { padding: "7px 6px", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 12 };
  return (
    <div className="r-scroll-x" style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 640 }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border-subtle)" }}>
            <th className="eyebrow" style={{ ...th, textAlign: "left", padding: "0 6px 8px 0" }}>Week of</th>
            {FUNNEL_STAGES.map((stage) => (
              <th key={stage} className="eyebrow" style={th} title={STAGE_LABEL[stage]}>
                {STAGE_SHORT[stage]}
              </th>
            ))}
            <th className="eyebrow" style={th} title="Median time from signup to first unlock">To unlock</th>
          </tr>
        </thead>
        <tbody>
          {cohorts.map((c) => {
            const base = c.counts.signed_up;
            return (
              <tr key={c.week} style={{ borderBottom: "1px solid var(--border-subtle)", opacity: base === 0 ? 0.55 : 1 }}>
                <td style={{ padding: "7px 6px 7px 0", color: "var(--text-primary)", whiteSpace: "nowrap" }}>{weekLabel(c.week)}</td>
                {FUNNEL_STAGES.map((stage) => {
                  const v = c.counts[stage];
                  return (
                    <td key={stage} className="tnum" style={{ ...td, color: v > 0 ? "var(--text-primary)" : "var(--text-muted)" }}>
                      {stage === "signed_up" ? (
                        v.toLocaleString()
                      ) : (
                        <>
                          {v.toLocaleString()}
                          <span style={{ color: "var(--text-muted)", marginLeft: 4, fontSize: 11 }}>{base > 0 ? pct(v, base) : ""}</span>
                        </>
                      )}
                    </td>
                  );
                })}
                <td className="tnum" style={{ ...td, color: "var(--text-muted)" }}>{formatHours(c.medianUnlockHours)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Stuck lists ──────────────────────────────────────────────────────────────

function StuckList({ users, emptyText, meta }: { users: FunnelStuckUser[]; emptyText: string; meta: (u: FunnelStuckUser) => string }) {
  if (users.length === 0) {
    return <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>{emptyText}</p>;
  }
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column" }}>
      {users.map((u) => (
        <li
          key={u.id}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            padding: "8px 0",
            borderBottom: "1px solid var(--border-subtle)",
            minWidth: 0,
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Link
              to="/admin/users"
              search={{ q: u.email }}
              style={{ fontSize: 13, color: "var(--text-primary)", textDecoration: "none", fontWeight: 500, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {u.name ?? (isPlaceholderEmail(u.email) ? "No email yet" : u.email)}
            </Link>
            <div style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {u.name ? `${isPlaceholderEmail(u.email) ? "No email yet" : u.email} · ` : ""}
              {meta(u)}
            </div>
          </div>
          <span style={{ fontSize: 11, color: "var(--text-muted)", whiteSpace: "nowrap" }}>
            seen {formatRelative(u.lastSeenAt).toLowerCase()}
          </span>
        </li>
      ))}
    </ul>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminFunnelPage() {
  const { user } = useAuth();
  const [weeks, setWeeks] = React.useState<Weeks>("12");
  const enabled = !!user?.isAdmin;

  const funnelQuery = useQuery({
    queryKey: ["admin/funnel", weeks],
    queryFn: () => fetchAdminFunnel(Number(weeks)),
    enabled,
    staleTime: 60_000,
  });

  if (!user?.isAdmin) return <AccessDenied />;

  const funnel = funnelQuery.data?.funnel;
  const totals = funnel?.totals;
  const nothingYet = funnel != null && funnel.totals.signed_up === 0;
  const signedUpAgo = (u: FunnelStuckUser) => `signed up ${formatRelative(u.createdAt).toLowerCase()}`;
  const processedAgo = (u: FunnelStuckUser) => `first import ${formatRelative(u.firstProcessedAt).toLowerCase()}`;

  return (
    <AdminShell
      title="Activation funnel"
      action={
        <Segmented
          value={weeks}
          options={[
            { value: "4", label: "4 weeks" },
            { value: "8", label: "8 weeks" },
            { value: "12", label: "12 weeks" },
            { value: "26", label: "26 weeks" },
          ]}
          onChange={setWeeks}
        />
      }
    >
      {/* ── Headline ─────────────────────────────────────────────────── */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        {totals ? (
          <>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Signed up" value={totals.signed_up.toLocaleString()} sub={`last ${funnel.weeks} weeks, admins excluded`} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Source connected" value={pct(totals.source_connected, totals.signed_up)} sub={`${totals.source_connected.toLocaleString()} of ${totals.signed_up.toLocaleString()}`} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="First unlock" value={pct(totals.first_unlock, totals.signed_up)} sub={`${totals.first_unlock.toLocaleString()} of ${totals.signed_up.toLocaleString()}`} gold />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Median time to first unlock" value={formatHours(funnel.medianUnlockHours)} sub={funnel.unlockedUsers > 0 ? `${funnel.unlockedUsers.toLocaleString()} unlocked` : "nobody has unlocked yet"} />
            </AtlasPanel>
            <AtlasPanel bodyStyle={{ padding: "16px 18px" }}>
              <StatBlock size="sm" label="Returned day 7" value={pct(totals.returned_day7, totals.signed_up)} sub="any foreground session 7+ days after signup" />
            </AtlasPanel>
          </>
        ) : (
          <>
            <Skel height={84} />
            <Skel height={84} />
            <Skel height={84} />
            <Skel height={84} />
            <Skel height={84} />
          </>
        )}
      </div>

      {nothingYet && (
        <AtlasPanel>
          <EmptyState
            icon={<Filter size={20} />}
            title="No signups in this window"
            hint="Widen the window, or wait for the first non-admin accounts to arrive."
          />
        </AtlasPanel>
      )}

      {/* ── Stage bars + cohort table ─────────────────────────────────── */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 3fr)", gap: 16 }}>
        <AtlasPanel title="Stages">
          {totals ? <StageBars totals={totals} /> : <Skel height={220} />}
        </AtlasPanel>

        <AtlasPanel title="Weekly cohorts">
          {funnel ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <CohortTable cohorts={funnel.cohorts} />
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
                Counts per signup week with the share of that cohort. Returned day 7 needs a week to become possible, so the newest rows read low. Weeks start on Monday.
              </p>
            </div>
          ) : (
            <Skel height={220} />
          )}
        </AtlasPanel>
      </div>

      {/* ── Stuck ────────────────────────────────────────────────────── */}
      <div className="r-cols-2" style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 16 }}>
        <AtlasPanel title="No source after signup" action={funnel ? <span className="eyebrow" style={{ fontSize: 10 }}>{funnel.stuck.noSource.length}{funnel.stuck.noSource.length >= funnel.stuck.limit ? "+" : ""}</span> : null}>
          {funnel ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <StuckList users={funnel.stuck.noSource} emptyText="Everyone in this window who is older than three days has connected something." meta={signedUpAgo} />
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
                Signed up more than {funnel.stuck.noSourceDays} days ago, no integration and no GPX upload. Newest first, capped at {funnel.stuck.limit}. Click a name to open them in Users.
              </p>
            </div>
          ) : (
            <Skel height={160} />
          )}
        </AtlasPanel>

        <AtlasPanel title="Imported but never came back" action={funnel ? <span className="eyebrow" style={{ fontSize: 10 }}>{funnel.stuck.noReturn.length}{funnel.stuck.noReturn.length >= funnel.stuck.limit ? "+" : ""}</span> : null}>
          {funnel ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <StuckList users={funnel.stuck.noReturn} emptyText="Everyone whose first import finished has been back since." meta={processedAgo} />
              <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0, lineHeight: 1.5 }}>
                First activity processed over {funnel.stuck.returnGapHours} hours ago and no foreground session since. Presence tracking began with spec 14, so older accounts can read as stuck.
              </p>
            </div>
          ) : (
            <Skel height={160} />
          )}
        </AtlasPanel>
      </div>
    </AdminShell>
  );
}
